import { useEffect, useState } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';

import { useTranslation } from '@hooks';
import {
    Breadcrumb,
    DesktopBuyMenu,
    DetailsModal,
    MobileDetailsTab,
    ProductColors,
    SizePicker,
} from '@components/products';
import { DefectiveSaleWarning } from '@components/ui';
import { Text, H3, Text2, H1 } from '@components/styles/Typography';
import { HideTablet, StyledRow } from '@components/styles/CommonStyles';
import { useRouter } from 'next/router';
import { capitalizeSentences } from '@utils';

const MobileBuyMenu = dynamic(() => import('./MobileBuyMenu'), { ssr: true });

const ProductDetail = ({ product, onProductZoom }) => {
    const router = useRouter();
    const isProdRequest = !!router.query['production-request'];
    const [showError, setShowError] = useState(false);
    const { t, locale, useCurrency } = useTranslation();
    const [selectedSize, setSelectedSize] = useState('');
    const [currentTab, setCurrentTab] = useState(false);
    const [detailsModalIsOpen, setDetailsModalIsOpen] = useState(false);
    const [discountRate, setDiscountRate] = useState(product.discount_rate);
    const [showWarning, setShowWarning] = useState(false);
    const [stockModalValue, setStockModalValue] = useState(false);
    const [variantQuantity, setVariantQuantity] = useState([]);
    const { variants, id, sku, attribute_detail: attributeDetail } = product;
    const isDefective = sku.includes('-D');

    const closeDetailsModal = () => {
        setDetailsModalIsOpen(false);
        setCurrentTab(false);
    };
    const handleCurrentTab = (tab) => setCurrentTab(tab);

    const openInfoModal = () => handleCurrentTab('details');

    const openReturnPolicyModal = () => handleCurrentTab('returnPolicy');

    const priceBySize = (priceType) => {
        if (selectedSize) {
            return product?.sizes.find((s) => s.id === selectedSize)[priceType];
        }
        return product[priceType];
    };

    useEffect(() => {
        if (selectedSize) {
            setDiscountRate(
                product.sizes.find((s) => s.id === selectedSize).discount_rate,
            );
        }
    }, [selectedSize]);

    const discountedPrice = useCurrency(priceBySize('discounted_price'));

    const price = useCurrency(priceBySize('price'));

    if (showError) {
        setTimeout(() => {
            setShowError(false);
        }, 5000);
    }

    const renderReminderText = () => {
        if (isProdRequest) {
            return 'Stock Availabity : Min 10 Packs ';
        }
        if (showError) {
            return `Stock Availabity : ${showError} Packs`;
        }

        return '1 PACK = 3 UNITS';
    };

    return (
        <ProductDetailContainer>
            <ProductDetailWrapper>
                <FeaturesContainer>
                    <Breadcrumb
                        responsiveCenter
                        parentName={product?.category[0]?.parent?.name[locale]}
                        parentSlug={product?.category[0]?.parent?.slug[locale]}
                        productName={product?.name[locale]}
                        subCategoryName={product?.category[0]?.name[locale]}
                        subCategorySlug={product?.category[0]?.slug[locale]}
                    />
                    <HideTablet>
                        <TextContainer>
                            <H1>
                                {capitalizeSentences(product?.name[locale])}
                            </H1>
                        </TextContainer>
                        <PriceContainer>
                            {discountRate > 0 ? (
                                <StyledRow
                                    justifyContent="flex-start"
                                    margin="0 0 8px 0"
                                >
                                    <H3>{discountedPrice}</H3>
                                    <DiscountRateWrapper>
                                        <H3 color="secondary">
                                            -%{discountRate}
                                        </H3>
                                    </DiscountRateWrapper>
                                </StyledRow>
                            ) : (
                                <DiscountedPriceWrapper>
                                    <Text>{price}</Text>
                                </DiscountedPriceWrapper>
                            )}
                            {discountRate > 0 && (
                                <PriceWrapper>
                                    <Text color="bgGray25">{price}</Text>
                                </PriceWrapper>
                            )}
                        </PriceContainer>
                    </HideTablet>
                    {isDefective && (
                        <DefectiveSaleWarning text={t('defectiveWarning')} />
                    )}
                    <Text2 style={{ margin: '5px 0' }}>Unit Code: {sku}</Text2>
                    <ProductColors
                        withTitle
                        colorsPhoto={variants}
                        productID={id}
                        currentColor={product?.colour?.name}
                    />
                    <PackReminder>
                        <Text
                            size="14px"
                            color={showError ? '#000000' : '#7F7F7F'}
                        >
                            {renderReminderText()}
                        </Text>
                    </PackReminder>
                    <SizePickerWrapper>
                        <SizePicker
                            testId="size-picker-mobile"
                            optionTestId="size-option-mobile"
                            selectedSize={selectedSize}
                            setSelectedSize={setSelectedSize}
                            showWarning={showWarning}
                            modelID={product.id}
                            sizes={product?.sizes}
                            stock={product?.stock}
                            product={product}
                            setShowWarning={setShowWarning}
                            stockModalValue={stockModalValue}
                            setStockModalValue={setStockModalValue}
                            showError={showError}
                            setShowError={setShowError}
                            setVariantQuantity={setVariantQuantity}
                            variantQuantity={variantQuantity}
                            hideButton
                        />
                    </SizePickerWrapper>
                </FeaturesContainer>
                <MobileDetailsTab details={attributeDetail} sku={sku} />
                <>
                    <DesktopBuyMenu
                        discountRate={discountRate}
                        product={product}
                        selectedSize={selectedSize}
                        setSelectedSize={setSelectedSize}
                        openInfoModal={openInfoModal}
                        setShowError={setShowError}
                        setVariantQuantity={setVariantQuantity}
                        variantQuantity={variantQuantity}
                        openReturnPolicyModal={openReturnPolicyModal}
                    />

                    <MobileBuyMenu
                        discountRate={discountRate}
                        product={product}
                        selectedSize={selectedSize}
                        setSelectedSize={setSelectedSize}
                        onProductZoom={onProductZoom}
                        showWarning={showWarning}
                        setShowWarning={setShowWarning}
                        showError={showError}
                        setShowError={setShowError}
                        stockModalValue={stockModalValue}
                        setStockModalValue={setStockModalValue}
                        setVariantQuantity={setVariantQuantity}
                        variantQuantity={variantQuantity}
                    />
                </>
                <ButtonWrapper>
                    <Button onClick={openInfoModal} type="button">
                        <Text2>{t('productDetails')}</Text2>
                    </Button>
                    <Button onClick={openReturnPolicyModal} type="button">
                        <Text2>{t('returnPolicy')}</Text2>
                    </Button>
                    <DetailsModal
                        currentTab={currentTab}
                        closeDetailsModal={closeDetailsModal}
                        detailsModalIsOpen={detailsModalIsOpen}
                        details={attributeDetail}
                        sku={sku}
                    />
                </ButtonWrapper>
            </ProductDetailWrapper>
        </ProductDetailContainer>
    );
};

const ProductDetailContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 95px 40px 10px 121px;
    width: 100%;
    max-height: calc(100vh + 100px);
    position: sticky;
    top: 0;
    bottom: 0;

    @media (max-width: 1024px) {
        padding: 25px 0;
        max-height: unset;
        position: unset;
        overflow: hidden;
        max-height: calc(100vh);
    }
`;

const ProductDetailWrapper = styled.div`
    width: 100%;
    overflow: hidden;
`;
const SizePickerWrapper = styled.div`
    display: none;

    @media (max-width: 1025px) {
        display: block;
        margin-top: 24px;
    }
`;
const FeaturesContainer = styled.div`
    @media (max-width: 1024px) {
        padding: 0 15px;
        max-height: unset;
        position: unset;
        overflow: hidden;
    }
`;
const TextContainer = styled.div`
    margin: 18px 0;
    h1 {
        font-weight: ${({ theme }) => theme.fontWeights.medium};
        font-size: ${({ theme }) => theme.sizes.l};
        color: ${({ theme, color }) =>
            color ? theme.colors[color] : theme.colors.primary};
        line-height: 26px;
        letter-spacing: 3px;
        text-transform: ${({ uppercase }) =>
            uppercase ? 'uppercase' : 'none'};
    }
`;
const PriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
`;
const DiscountRateWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.colors.primary};
    padding: 0 6px;
    margin-left: 16px;
`;
const DiscountedPriceWrapper = styled.div`
    margin: 0;
`;

const Button = styled.button`
    background: none;
    border: none;
    cursor: pointer;

    :first-child {
        margin-right: 40px;
    }
`;
const PriceWrapper = styled.div`
    p {
        text-decoration: line-through;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    margin-top: 30px;
    width: calc(100% - 188px);
    margin-left: auto;

    @media (max-width: 1024px) {
        display: none;
    }
`;
const PackReminder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 8px 0;
    margin: 17px 0;
    background: #f4f4f4;
`;

export default ProductDetail;
